import {
  ConnectorDataLocation,
  ConnectorSpec,
  OperationListItem,
} from '@integration-app/sdk'
import ConnectorPage from 'routes/Connectors/Connector'
import {
  CONNECTOR_REVALIDATE_TIME,
  CONNECTORS_API,
  LINKS,
} from 'routes/constants'

export default function Connector({
  connector,
  connectorOperations,
  connectorData,
  similarConnectors,
}: {
  connector: ConnectorSpec
  connectorOperations: OperationListItem[]
  connectorData: ConnectorDataLocation[]
  similarConnectors: Array<ConnectorSpec>
}) {
  return (
    <ConnectorPage
      connector={connector}
      connectorOperations={connectorOperations}
      connectorData={connectorData}
      similarConnectors={similarConnectors}
    />
  )
}

export async function getStaticPaths() {
  // Fetch data from external API
  const res = await fetch(`${CONNECTORS_API}?all=1`)
  const connectors = (await res.json())
    .filter((c) => c.status === 'production')
    .map((c) => c.key)

  return {
    paths: connectors.map(
      (key) => ({
        params: {
          connectorKey: key,
        },
      }), // See the "paths" section below
    ),
    fallback: true, // false or "blocking"
  }
}

export async function getStaticProps({ params }) {
  // Fetch data from external API
  const connectorId = params.connectorKey

  try {
    const [connector, connectorOperations, connectorData, similarConnectors] =
      await Promise.all([
        fetchConnector(connectorId),
        fetchConnectorOperations(connectorId),
        fetchConnectorData(connectorId),
        fetchSimilarConnectors(connectorId),
      ])

    // Pass data to the page via props
    return {
      props: {
        connector,
        connectorOperations,
        connectorData,
        similarConnectors,
      },
      revalidate: CONNECTOR_REVALIDATE_TIME, // In seconds
    }
  } catch (e) {
    // `redirect` can not be returned from getStaticProps during pre-rendering
    // During pre-rendering we can only return `notFound`
    if (process.env.npm_lifecycle_event === 'build') {
      return {
        notFound: true,
      }
    }
    return {
      redirect: {
        destination: LINKS.CONNECTORS,
        permanent: false,
      },
    }
  }
}

async function getConnectorData(path) {
  const res = await fetch(`${CONNECTORS_API}/${path}`)

  if (res.status !== 200) {
    throw new Error(`Connector not found: ${path}`)
  }

  const connectorData = await res.json()
  return connectorData
}

async function fetchConnector(connectorKey) {
  return getConnectorData(`${connectorKey}`)
}

async function fetchConnectorOperations(connectorKey) {
  return getConnectorData(`${connectorKey}/operations`)
}

async function fetchConnectorData(connectorKey) {
  return getConnectorData(`${connectorKey}/data`)
}

async function fetchSimilarConnectors(connectorKey) {
  const similarConnectors = await getConnectorData(`${connectorKey}/similar`)

  await Promise.all(
    (similarConnectors ?? []).map(async (connector: any) => {
      const res = await fetch(`${CONNECTORS_API}/${connector.id}/udm`)
      connector.udm = await res.json()
    }),
  )

  return similarConnectors
}
